import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { CompanyService } from 'src/app/admin/services/company.service';
import { CompanyContextService } from '../../services/company-context.service';
import { CurrentUserService } from '../../services/current-user.service';
import { Company } from 'src/app/user/model/user';

@Component({
  selector: 'app-company-context-selector',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SelectModule,
    ButtonModule,
    TooltipModule,
    TranslocoModule,
  ],
  templateUrl: './company-context-selector.component.html',
})
export class CompanyContextSelectorComponent implements OnInit {
  private companyService = inject(CompanyService);
  private translocoService = inject(TranslocoService);
  companyContextService = inject(CompanyContextService);
  currentUserService = inject(CurrentUserService);

  @Input() isCollapsed = false;
  @Output() expandRequest = new EventEmitter<void>();

  companies: { value: string; label: string; company: Company }[] = [];
  selectedCompanyId: string | null = null;
  isLoading = true;

  ngOnInit() {
    this.loadCompanies();
  }

  private loadCompanies() {
    this.isLoading = true;

    this.companyService.getCompanyList().subscribe({
      next: (companies: Company[]) => {
        this.companies = companies
          .map((company) => ({
            value: company.id!,
            label: company.name || 'Unnamed Company',
            company,
          }))
          .sort((a, b) => a.label.localeCompare(b.label));

        // Set current selection from persisted state
        const actingCompany = this.companyContextService.actingCompany();
        if (actingCompany?.id) {
          this.selectedCompanyId = actingCompany.id;
          // Update the full company object if we only had the ID
          const fullCompany = this.companies.find(
            (c) => c.value === actingCompany.id,
          );
          if (fullCompany) {
            this.companyContextService.setActingCompany(fullCompany.company);
          }
        } else {
          // Default to own company
          this.selectedCompanyId = this.currentUserService.ownCompanyId() || null;
        }

        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading companies:', error);
        this.isLoading = false;
      },
    });
  }

  onCompanyChange(companyId: string | null) {
    if (!companyId) {
      this.companyContextService.clearActingCompany();
      return;
    }

    const selectedCompanyData = this.companies.find(
      (c) => c.value === companyId,
    );

    if (selectedCompanyData) {
      const ownCompanyId = this.currentUserService.ownCompanyId();

      if (companyId === ownCompanyId) {
        // Selected own company - clear acting company
        this.companyContextService.clearActingCompany();
      } else {
        // Selected another company
        this.companyContextService.setActingCompany(
          selectedCompanyData.company,
        );
      }

      // Reload the page to refresh data with new context
      window.location.reload();
    }
  }

  get isActingAsOther(): boolean {
    return this.companyContextService.isActingAsOtherCompany();
  }

  get selectedCompanyName(): string {
    const selected = this.companies.find((c) => c.value === this.selectedCompanyId);
    return selected?.label || '';
  }

  get tooltipText(): string {
    const actingAs = this.translocoService.translate('admin.actingAs');
    const companyName = this.selectedCompanyName || '...';
    return `${actingAs}: ${companyName}`;
  }

  onCollapsedClick(): void {
    this.expandRequest.emit();
  }
}
